import axios from "api/axios";
import {
  deleteAppointmentChecklistEntry,
  patchAppointmentChecklistEntry,
  postAppointmentChecklistEntry,
} from "features/requests/api/appointmentChecklistEntries";
import { getAppointmentChecklists } from "features/requests/api/getAppointmentChecklists";
import type { IChecklistWithResponses } from "features/requests/types/checklists";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import type { TChecklistPickerRow } from "components/checklist_dropdown";

// ============================================================================
// Types
// ============================================================================

export type TChecklistTemplateRow = {
  id: number;
  fk_appointment_type_id: number | null;
  fk_sub_appointment_type_id: number | null;
  label: string | null;
  order: number | null;
  required: boolean | null;
};

/**
 * Single row in the checklist panel. Combines existing entries (entry_id
 * != null) and would-be POSTs (entry_id == null) under one shape, plus the
 * `originalLabel`/`originalRequired` snapshot used by `flushPending` to
 * detect patches against the BE.
 */
export type ChecklistRow = {
  /** Stable React key. */
  key: string;
  /** Underlying eservbusiness.checklist.id (used for POST + lookups). */
  template_id: number;
  /** Saved entry id, or null if this row is a would-be POST. */
  entry_id: number | null;
  /** Label/required from the BE — used to detect patches against the original. */
  originalLabel: string;
  originalRequired: boolean | null;
  originalOrder: number | null;
  label: string;
  required: boolean | null;
  order: number;
  is_done: boolean;
  /** True if the user dismissed this row. Hidden from display, DELETEd on save. */
  dismissed: boolean;
};

export type UseChecklistRowsOptions = {
  appointmentIdNum: number | null;
  /** Normalized: `null` when the type is empty (Number("") === 0 footgun, audit 005 §F2). */
  actualTypeOrNull: number | null;
  defaultNum: number | null;
  /** "(No auto-fill)" with a sub-default picker means the user opted out — drop placeholder rows. */
  allowChecklistAutoOffer: boolean;
  /**
   * The appointment's saved (type, default) scope on the BE — derived directly
   * from the `appointment` prop in the parent. This is the source of truth for
   * "is the FE-current scope still the appointment's saved scope?" Pass `null`
   * for create-mode or when the appointment isn't loaded yet.
   *
   * Avoid using a ref-capture: capturing from the FE scope at first render
   * (or after a flush reset) drifts whenever the user picks a different
   * default before saving, which breaks the diverged-vs-saved branch.
   */
  savedScope: { type: number | null; default: number | null } | null;
};

export type ChecklistsApi = {
  rows: ChecklistRow[];
  /** Rows with `dismissed=false`. Use for both render and the panel count. */
  displayRows: ChecklistRow[];
  /** Templates in scope not yet on the appointment — drives the "(No auto-fill)" hint UI. */
  scopedTemplatesNotYetAdded: TChecklistTemplateRow[];
  isLoading: boolean;
  isError: boolean;
  templatesIsLoading: boolean;
  handleLabelChange: (row: ChecklistRow, nextLabel: string) => void;
  handleRequiredChange: (row: ChecklistRow, checked: boolean) => void;
  handleDismissRow: (row: ChecklistRow) => void;
  handleUndoPatchesForRow: (row: ChecklistRow) => void;
  handleConfirmTemplatesFromPicker: (picked: TChecklistPickerRow[]) => void;
  handleReorder: (from: number, to: number) => void;
  flushPending: (apptId: number) => Promise<void>;
};

// ============================================================================
// Helper: scope-match predicate
// ============================================================================

const templateMatchesAppointmentScope = (
  row: TChecklistTemplateRow,
  typeId: number,
  defaultId: number | null
) => {
  if (Number(row.fk_appointment_type_id) !== typeId) return false;
  if (defaultId == null) {
    return row.fk_sub_appointment_type_id == null;
  }
  return Number(row.fk_sub_appointment_type_id) === defaultId;
};

/** Sequential 1-based `order` when `nextRows` is already in desired display sequence. */
const normalizeRowsOrder = (nextRows: ChecklistRow[]) =>
  nextRows.map((row, index) => ({ ...row, order: index + 1 }));

const syncBaselineFromDisplay = (row: ChecklistRow): ChecklistRow => ({
  ...row,
  originalLabel: row.label,
  originalRequired: row.required,
  originalOrder: row.order,
});

const finalizeVisibleOrderAndBaseline = (nextRows: ChecklistRow[]) =>
  normalizeRowsOrder(nextRows).map(syncBaselineFromDisplay);

const rowsOrderedForPersist = (nextRows: ChecklistRow[]) => {
  const visible = nextRows
    .filter((r) => !r.dismissed)
    .sort((a, b) => a.order - b.order);
  const reindexed = visible.map((row, i) => ({ ...row, order: i + 1 }));
  const dismissed = nextRows.filter((r) => r.dismissed);
  return [...reindexed, ...dismissed];
};

// ============================================================================
// Hook
// ============================================================================

/**
 * Owns the appointment-modal's checklist row state. Phase 2b lift of the
 * state machine that previously lived inside `AppointmentModalChecklistsSection`.
 *
 * Two queries:
 *   - `checklistQuery` — `GET /api/v1/company/appointment-checklists/:id`,
 *     returns rows scoped to the appointment's *saved* type/default.
 *   - `templatesQuery` — `GET /api/v1/eserv-business/checklist`, all company
 *     templates. Used for client-side filtering when the FE scope diverges
 *     from `savedScope`.
 *
 * Rebuild rules (audit 005 §10, audit 006 §14.Q1–Q4):
 *   - At saved scope: V2 already returns the right set. Use it directly,
 *     skipping placeholders when `allowChecklistAutoOffer` is false.
 *   - Diverged from saved scope: existing entries (entry_id != null) belong
 *     to the old scope and DELETE on save → mark dismissed. Then add
 *     new-scope templates as pending POSTs (when autofill is allowed).
 *
 * `flushPending` syncs `rows` to the BE after a fresh GET (covers seed-before-flush):
 *   - Server entries not claimed by a visible row → DELETE.
 *   - Claimed rows differing from server snapshot → PATCH (resolves entry_id from template after seed).
 *   - No server row for template → POST.
 */
export function useChecklistRows(opts: UseChecklistRowsOptions): ChecklistsApi {
  const {
    appointmentIdNum,
    actualTypeOrNull,
    defaultNum,
    allowChecklistAutoOffer,
    savedScope,
  } = opts;

  const { t } = useTranslation();
  const tBase = "containers.appointmentCard.appointment-folder.appointmentModal";
  const queryClient = useQueryClient();

  const [rows, setRows] = useState<ChecklistRow[]>([]);

  const checklistQuery = useQuery(
    ["appointment-checklists", appointmentIdNum ?? "new"],
    () => getAppointmentChecklists(appointmentIdNum as number),
    {
      enabled: appointmentIdNum != null,
      refetchOnWindowFocus: false,
      refetchOnMount: "always",
    }
  );

  // Single cache slot per company. The `/eserv-business/checklist` endpoint
  // returns ALL templates regardless of params, so keying by (type, default)
  // would force a refetch every time the FE scope changed — and during that
  // refetch the build effect's `templatesQuery.isLoading` gate would early-
  // return, leaving `rows` stale. (Observed regression on create flow:
  // picking a sub-default invalidated the cache, the rebuild stalled, and a
  // quick Submit POSTed the prior parent rows against a sub-scoped
  // appointment — entries mismatched the saved scope.) Filtering happens
  // client-side in `allScopedTemplates` instead.
  const templatesQuery = useQuery(
    ["eserv-business-checklists", "appointment-modal-scope"],
    async () => {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/checklist`
      );
      const raw = Array.isArray(res.data) ? res.data : [];
      return raw.map((row: TChecklistTemplateRow) => ({
        ...row,
        required: row.required === true ? true : row.required === false ? false : null,
      })) as TChecklistTemplateRow[];
    },
    { enabled: actualTypeOrNull != null, refetchOnWindowFocus: false }
  );

  /** All templates matching the current FE scope (used for FE scope-divergence rebuilds). */
  const allScopedTemplates = useMemo(() => {
    if (actualTypeOrNull == null) return [];
    const data = templatesQuery.data ?? [];
    return data.filter((r) => templateMatchesAppointmentScope(r, actualTypeOrNull, defaultNum));
  }, [actualTypeOrNull, defaultNum, templatesQuery.data]);

  /** Templates not yet on the appointment — drives the "no autofill" hint UI. */
  const scopedTemplatesNotYetAdded = useMemo(() => {
    const blocked = new Set<number>();
    for (const r of rows) {
      if (!r.dismissed) blocked.add(r.template_id);
    }
    return allScopedTemplates.filter((r) => r.id != null && !blocked.has(r.id));
  }, [allScopedTemplates, rows]);

  // -----------------------------------------------------------------
  // Deterministic rebuild (Phase 2a logic, lifted here in 2b)
  // -----------------------------------------------------------------

  useEffect(() => {
    const untitled = t(`${tBase}.Checklist untitled`);

    // -------- New appointment (no V2 fetch possible yet) --------
    // The V2 endpoint requires an appointment id, so for create-mode the
    // section pulls scoped templates straight from `templatesQuery`. They
    // are POSTed as new entries inside `flushPending` after the BE returns
    // the new appointment id from `saveAppt`'s create response.
    if (appointmentIdNum == null) {
      if (templatesQuery.isLoading) return;

      const next: ChecklistRow[] = [];
      if (allowChecklistAutoOffer && actualTypeOrNull != null) {
        const orderedTemplates = [...allScopedTemplates].sort(
          (a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
        )
        let i = 0
        for (const tmpl of orderedTemplates) {
          next.push({
            key: `pending-${actualTypeOrNull}-${defaultNum ?? "p"}-${tmpl.id}-${i++}`,
            template_id: tmpl.id,
            entry_id: null,
            originalLabel: tmpl.label ?? "",
            originalRequired: tmpl.required ?? null,
            originalOrder: null,
            label: tmpl.label?.trim() || untitled,
            required: tmpl.required ?? null,
            order: next.length + 1,
            is_done: false,
            dismissed: false,
          });
        }
      }
      setRows(finalizeVisibleOrderAndBaseline(next));
      return;
    }

    // -------- Existing appointment --------
    if (!checklistQuery.isSuccess) return;

    // savedScope is the appointment's BE scope (passed from the parent). The
    // FE diverges when the user picks a different (type, default) without
    // saving. Treat them as the "saved" scope when they match.
    const isAtSavedScope =
      savedScope == null ||
      (savedScope.type === actualTypeOrNull && savedScope.default === defaultNum);

    if (!isAtSavedScope && templatesQuery.isLoading) return;

    const next: ChecklistRow[] = [];

    if (isAtSavedScope) {
      const orderedEntries = [...(checklistQuery.data ?? [])].sort(
        (a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
      )
      for (const e of orderedEntries) {
        const isPlaceholder = e.entry_id == null;
        // Q3=B: when the user opted out via "(No auto-fill)", drop placeholders.
        if (isPlaceholder && !allowChecklistAutoOffer) continue;
        next.push({
          key: isPlaceholder ? `tmpl-${e.id}` : `entry-${e.entry_id}`,
          template_id: e.id,
          entry_id: e.entry_id ?? null,
          originalLabel: e.label ?? "",
          originalRequired: e.required ?? null,
          originalOrder: e.order ?? null,
          label: e.label?.trim() || untitled,
          required: e.required ?? null,
          order: e.order ?? next.length + 1,
          is_done: Boolean(e.is_done),
          dismissed: false,
        });
      }
    } else {
      const orderedEntries = [...(checklistQuery.data ?? [])].sort(
        (a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
      )
      for (const e of orderedEntries) {
        if (e.entry_id == null) continue;
        next.push({
          key: `entry-${e.entry_id}`,
          template_id: e.id,
          entry_id: e.entry_id,
          originalLabel: e.label ?? "",
          originalRequired: e.required ?? null,
          originalOrder: e.order ?? null,
          label: e.label ?? "",
          required: e.required ?? null,
          order: e.order ?? next.length + 1,
          is_done: Boolean(e.is_done),
          dismissed: true,
        });
      }
      if (allowChecklistAutoOffer && actualTypeOrNull != null) {
        const orderedTemplates = [...allScopedTemplates].sort(
          (a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
        )
        let i = 0
        for (const tmpl of orderedTemplates) {
          next.push({
            key: `pending-${actualTypeOrNull}-${defaultNum ?? "p"}-${tmpl.id}-${i++}`,
            template_id: tmpl.id,
            entry_id: null,
            originalLabel: tmpl.label ?? "",
            originalRequired: tmpl.required ?? null,
            originalOrder: null,
            label: tmpl.label?.trim() || untitled,
            required: tmpl.required ?? null,
            order: next.length + 1,
            is_done: false,
            dismissed: false,
          });
        }
      }
    }

    setRows(finalizeVisibleOrderAndBaseline(next));
  }, [
    appointmentIdNum,
    checklistQuery.isSuccess,
    checklistQuery.data,
    templatesQuery.isLoading,
    templatesQuery.data,
    allScopedTemplates,
    actualTypeOrNull,
    defaultNum,
    allowChecklistAutoOffer,
    savedScope,
    t,
  ]);

  const displayRows = useMemo(
    () =>
      rows
        .filter((r) => !r.dismissed)
        .sort((a, b) => a.order - b.order),
    [rows]
  );

  // -----------------------------------------------------------------
  // Save flow — fresh GET + reconcile (handles seed-before-flush).
  // -----------------------------------------------------------------

  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const normFlushLabel = (v: string | null | undefined) => String(v ?? "").trim();
  const normFlushReq = (v: boolean | null | undefined) =>
    v === true ? true : v === false ? false : null;
  const normFlushOrder = (v: number | null | undefined) =>
    v != null && Number.isFinite(Number(v)) ? Number(v) : null;

  const flushPending = async (apptId: number) => {
    const currentRows = rowsOrderedForPersist(rowsRef.current);
    let didMutate = false;

    let serverRows = await getAppointmentChecklists(apptId);

    let didDelete = false;
    for (const s of serverRows) {
      if (s.entry_id == null) continue;
      const claimed = currentRows.some(
        (r) =>
          !r.dismissed &&
          (r.entry_id === s.entry_id ||
            (r.entry_id == null && r.template_id === s.id))
      );
      if (!claimed) {
        await deleteAppointmentChecklistEntry(apptId, s.entry_id);
        didDelete = true;
        didMutate = true;
      }
    }

    if (didDelete) {
      serverRows = await getAppointmentChecklists(apptId);
    }

    const serverWithEntryByTemplate = new Map<number, IChecklistWithResponses>();
    for (const s of serverRows) {
      if (s.entry_id != null) {
        serverWithEntryByTemplate.set(s.id, s);
      }
    }

    for (const r of currentRows) {
      if (r.dismissed) continue;
      const serverRow =
        r.entry_id != null
          ? serverRows.find((s) => s.entry_id === r.entry_id)
          : serverWithEntryByTemplate.get(r.template_id);
      if (serverRow?.entry_id != null) {
        const entryId = serverRow.entry_id;
        const labelDiff =
          normFlushLabel(r.label) !== normFlushLabel(serverRow.label);
        const reqDiff =
          normFlushReq(r.required) !== normFlushReq(serverRow.required ?? null);
        const ordDiff = r.order !== normFlushOrder(serverRow.order);
        if (labelDiff || reqDiff || ordDiff) {
          await patchAppointmentChecklistEntry(apptId, entryId, {
            label: r.label,
            required: r.required,
            order: r.order,
          });
          didMutate = true;
        }
        continue;
      }
      const customLabel = r.label !== r.originalLabel ? r.label : undefined;
      await postAppointmentChecklistEntry(apptId, r.template_id, r.order, customLabel);
      didMutate = true;
    }

    if (didMutate) {
      await queryClient.invalidateQueries(["appointment-checklists", apptId]);
      await queryClient.refetchQueries(["appointment-checklists", apptId], {
        active: true,
      });
    }
  };

  // -----------------------------------------------------------------
  // Row mutations
  // -----------------------------------------------------------------

  const updateRowByKey = (key: string, patch: Partial<ChecklistRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const handleLabelChange = (row: ChecklistRow, nextLabel: string) => {
    updateRowByKey(row.key, { label: nextLabel });
  };

  const handleRequiredChange = (row: ChecklistRow, checked: boolean) => {
    updateRowByKey(row.key, { required: checked });
  };

  const handleDismissRow = (row: ChecklistRow) => {
    if (row.entry_id == null) {
      // Pending POST — drop the row entirely so it doesn't reappear.
      setRows((prev) =>
        finalizeVisibleOrderAndBaseline(prev.filter((r) => r.key !== row.key))
      );
      return;
    }
    updateRowByKey(row.key, { dismissed: true });
  };

  const handleUndoPatchesForRow = (row: ChecklistRow) => {
    updateRowByKey(row.key, {
      label: row.originalLabel,
      required: row.originalRequired,
      ...(row.originalOrder != null ? { order: row.originalOrder } : {}),
    });
  };

  const handleReorder = (from: number, to: number) => {
    if (from === to) return;
    setRows((prev) => {
      const visibleRows = prev.filter((r) => !r.dismissed).sort((a, b) => a.order - b.order);
      if (
        from < 0 ||
        to < 0 ||
        from >= visibleRows.length ||
        to >= visibleRows.length
      ) {
        return prev;
      }
      const movedRows = [...visibleRows];
      const [moved] = movedRows.splice(from, 1);
      movedRows.splice(to, 0, moved);
      const movedByKey = new Map(movedRows.map((row, index) => [row.key, index + 1]));
      return prev.map((row) => {
        if (row.dismissed) return row;
        const nextOrder = movedByKey.get(row.key);
        return nextOrder == null ? row : { ...row, order: nextOrder };
      });
    });
  };

  /** Picker confirm — dedupe by template_id against visible rows. */
  const handleConfirmTemplatesFromPicker = (picked: TChecklistPickerRow[]) => {
    if (!picked.length) return;
    const untitled = t(`${tBase}.Checklist untitled`);
    setRows((prev) => {
      const blockedTemplateIds = new Set(
        prev.filter((r) => !r.dismissed).map((r) => r.template_id)
      );
      const next = [...prev];
      let i = next.length;
      for (const tmpl of picked) {
        if (blockedTemplateIds.has(tmpl.id)) continue;
        const label = tmpl.label?.trim() || untitled;
        next.push({
          key: `picker-${Date.now()}-${i++}`,
          template_id: tmpl.id,
          entry_id: null,
          originalLabel: tmpl.label ?? "",
          originalRequired:
            tmpl.required === true ? true : tmpl.required === false ? false : null,
          originalOrder: null,
          label,
          required:
            tmpl.required === true ? true : tmpl.required === false ? false : null,
          order: next.length + 1,
          is_done: false,
          dismissed: false,
        });
      }
      return finalizeVisibleOrderAndBaseline(next);
    });
  };

  return {
    rows,
    displayRows,
    scopedTemplatesNotYetAdded,
    isLoading: checklistQuery.isLoading,
    isError: checklistQuery.isError,
    templatesIsLoading: templatesQuery.isLoading,
    handleLabelChange,
    handleRequiredChange,
    handleDismissRow,
    handleUndoPatchesForRow,
    handleConfirmTemplatesFromPicker,
    handleReorder,
    flushPending,
  };
}
